import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Building2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface WorkspaceRow {
  id: string;
  name: string;
  slug: string | null;
  subscription_status: string | null;
  subscription_tier: string | null;
}

/**
 * Global admin search. Always searches workspaces by name/slug and links
 * straight to the workspace detail page.
 */
export function WorkspaceSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const { data: results, isFetching } = useQuery({
    queryKey: ["admin-global-workspace-search", debounced],
    queryFn: async () => {
      const term = debounced.replace(/[%,]/g, "");
      const { data, error } = await supabase
        .from("workspaces")
        .select("id, name, slug, subscription_status, subscription_tier")
        .or(`name.ilike.%${term}%,slug.ilike.%${term}%`)
        .order("name", { ascending: true })
        .limit(8);
      if (error) throw error;
      return (data || []) as WorkspaceRow[];
    },
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  const go = (id: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/admin/organizations/${id}`);
  };

  const items = results || [];

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && items[highlight]) { e.preventDefault(); go(items[highlight].id); }
          else if (e.key === "Escape") setOpen(false);
        }}
        placeholder="Search workspaces..."
        className="w-full bg-muted/50 pl-9 md:w-[300px] lg:w-[400px] border-transparent focus:bg-background focus:border-primary/20 transition-all"
      />
      {isFetching && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}

      {open && debounced.length >= 2 && (
        <div className="absolute left-0 top-11 z-50 w-full min-w-[320px] rounded-md border border-border bg-popover shadow-lg">
          {items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              {isFetching ? "Searching..." : "No workspaces found"}
            </p>
          ) : (
            <ul className="max-h-80 overflow-auto py-1">
              {items.map((w, i) => (
                <li key={w.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => go(w.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                      i === highlight ? "bg-muted" : "hover:bg-muted/60"
                    )}
                  >
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{w.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">{w.slug || w.id}</span>
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        "capitalize text-[10px]",
                        w.subscription_status === "active"
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600"
                          : "text-muted-foreground"
                      )}
                    >
                      {w.subscription_status || "none"}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
